'use client';

import Link from 'next/link';

export default function TermsOfServicePage() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .policy-root {
          font-family: 'DM Sans', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1a2e;
        }
        .policy-nav {
          background: #0d1b2a;
          padding: 0 32px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .policy-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .policy-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #00b4d8, #0077b6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .policy-logo-text { font-size: 20px; font-weight: 700; color: #ffffff; }
        .policy-logo-text span { color: #00b4d8; }
        .policy-back {
          color: #00b4d8;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }
        .policy-back:hover { opacity: 0.8; }
        .policy-hero {
          background: linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 100%);
          padding: 60px 32px;
          text-align: center;
        }
        .policy-hero h1 { font-size: 40px; font-weight: 700; color: #fff; margin: 0 0 12px; }
        .policy-hero p { color: #8b9ab0; font-size: 15px; margin: 0; }
        .policy-content { max-width: 860px; margin: 0 auto; padding: 60px 32px; }
        .policy-card {
          background: #fff;
          border-radius: 16px;
          padding: 48px;
          border: 1px solid #e8ecf0;
          box-shadow: 0 4px 24px rgba(0,0,0,0.04);
        }
        .policy-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 32px 0 12px;
          padding-top: 32px;
          border-top: 1px solid #f0f4f8;
        }
        .policy-card h2:first-child { margin-top: 0; padding-top: 0; border-top: none; }
        .policy-card p, .policy-card li { font-size: 15px; line-height: 1.8; color: #4b5563; margin: 0 0 12px; }
        .policy-card ul { padding-left: 20px; margin: 0 0 16px; }
        .policy-card li { margin-bottom: 8px; }
        .policy-updated {
          display: inline-block;
          background: rgba(0,180,216,0.1);
          border: 1px solid rgba(0,180,216,0.3);
          color: #00b4d8;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 99px;
          margin-bottom: 32px;
        }
        .policy-footer {
          background: #0d1b2a;
          padding: 24px 32px;
          text-align: center;
          color: #8b9ab0;
          font-size: 13px;
        }
        .policy-footer a { color: #00b4d8; text-decoration: none; }
        .policy-footer a:hover { text-decoration: underline; }
      `}</style>

      <div className="policy-root">
        <nav className="policy-nav">
          <Link href="/" className="policy-logo">
            <div className="policy-logo-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <span className="policy-logo-text">Cam<span>Smart</span></span>
          </Link>
          <Link href="/" className="policy-back">← Back to Store</Link>
        </nav>

        <div className="policy-hero">
          <h1>Terms of Service</h1>
          <p>Please read these terms carefully before using our website or purchasing products</p>
        </div>

        <div className="policy-content">
          <div className="policy-card">
            <span className="policy-updated">Last Updated: May 9, 2026</span>

            <h2>1. Acceptance of Terms</h2>
            <p>By accessing and using the CamSmart website (camsmart.com) and purchasing our products, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.</p>

            <h2>2. Products & Pricing</h2>
            <p>All products are subject to availability. We reserve the right to discontinue any product at any time. Prices are listed in US Dollars and are subject to change without notice. We strive to display accurate product information, but we do not warrant that product descriptions or prices are error-free.</p>
            <p>In the event of a pricing error, we reserve the right to cancel any orders placed at the incorrect price and notify you accordingly.</p>

            <h2>3. Orders & Payment</h2>
            <ul>
              <li>By placing an order, you represent that you are at least 18 years of age</li>
              <li>We accept major credit cards, debit cards, and other payment methods as displayed at checkout</li>
              <li>Payment is processed securely through our payment processor</li>
              <li>We reserve the right to refuse or cancel any order at our discretion</li>
              <li>You will receive an order confirmation email upon successful placement</li>
            </ul>

            <h2>4. Shipping & Delivery</h2>
            <p>We ship to addresses within the United States. Shipping times are estimates and not guaranteed. CamSmart is not responsible for delays caused by carriers, weather, or other circumstances beyond our control. Risk of loss passes to you upon delivery to the carrier.</p>

            <h2>5. Returns & Refunds</h2>
            <p>We offer a 30-day return policy on most items. To be eligible for a return:</p>
            <ul>
              <li>Item must be in original, unused condition with all original packaging</li>
              <li>Return must be initiated within 30 days of delivery</li>
              <li>Proof of purchase is required</li>
              <li>Customer is responsible for return shipping costs unless the item is defective</li>
            </ul>
            <p>Refunds will be processed within 5-10 business days after we receive and inspect the returned item. Please see our full <Link href="/refund-policy" style={{color:'#00b4d8'}}>Refund Policy</Link> for details.</p>

            <h2>6. Warranty</h2>
            <p>All CamSmart products come with a 2-year limited warranty against manufacturing defects. This warranty does not cover damage caused by misuse, accidents, unauthorized modifications, or normal wear and tear. Warranty claims must be submitted with proof of purchase.</p>

            <h2>7. Intellectual Property</h2>
            <p>All content on this website, including text, graphics, logos, images, and software, is the property of CamSmart and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.</p>

            <h2>8. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, CamSmart shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our products or website. Our total liability shall not exceed the amount you paid for the product giving rise to the claim.</p>

            <h2>9. Disclaimer of Warranties</h2>
            <p>Our website and products are provided "as is" without warranties of any kind, either express or implied, except as expressly stated in our warranty policy. We do not warrant that our website will be uninterrupted, error-free, or free of viruses.</p>

            <h2>10. Governing Law</h2>
            <p>These Terms of Service shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration in accordance with the American Arbitration Association rules.</p>

            <h2>11. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our website constitutes acceptance of the modified terms.</p>

            <h2>12. Contact Information</h2>
            <p>For questions about these Terms of Service, contact us at:<br /><strong>CamSmart</strong><br />Email: legal@camsmart.com<br />Phone: 1-800-CAM-SMART</p>
          </div>
        </div>

        <footer className="policy-footer">
          <p>© 2026 CamSmart. All rights reserved. &nbsp;|&nbsp; <Link href="/privacy-policy">Privacy Policy</Link> &nbsp;|&nbsp; <Link href="/terms-of-service">Terms of Service</Link> &nbsp;|&nbsp; <Link href="/disclaimer">Disclaimer</Link></p>
        </footer>
      </div>
    </>
  );
}
